import { typographyTokens } from '../../../theme/typographyTokens';
import React, { useState } from 'react';
import {
  useCreateDataFieldOptionTemplateMutation,
  useCreateDataFieldTemplateMutation,
  useGetDataFieldTemplatesQuery,
} from '../../../services/dataFieldsSlice';
import { Box, Button, Drawer, useTheme } from '@mui/material';

import AdminHeader from '../../../components/Admin/AdminHeader';
import AddButton from '../../../components/Admin/AddButton';
import ModalWidget from '../../../components/ModalWidget/ModalWidget';
import DataFieldScreenCard from '../../../components/Admin/DataFields/DataFieldScreenCard';
import AddDataField from '../../../components/Admin/DataFields/AddDataField';
import { DepartmentExtended } from '../../../../shared/types/Department';

import { CircularProgress } from '@mui/material';
import { DataField } from '../../../../shared/types/DataField';
import { DrawerFilters } from '../DrawerFilters/DrawerFilters';

const DataFields = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { data, isLoading } = useGetDataFieldTemplatesQuery();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [edit, setEdit] = useState<DepartmentExtended | undefined>(undefined);
  const [createDataField] = useCreateDataFieldTemplateMutation();
  const [createDataFieldOptions] = useCreateDataFieldOptionTemplateMutation();
  const [drawOpen, setDrawOpen] = useState<boolean>(false);
  const [filterData, setFilterData] = useState([]);
  const [appliedFilter, setAppliedFilter] = useState<any>(null);
  const [sortOrder, setSortOrder] = useState('normal');

  const handleEditTriggered = (e: DepartmentExtended) => {
    setEdit(e);
    setIsOpen(true);
  };

  const filterDataFields = (data: any, selectedItem: any) =>{
    setFilterData(data)
    setAppliedFilter(selectedItem)
  }

  const handleSortChange = (event: any) => {
    setSortOrder(event.target.value);
  };

  const sortedData = () => {
    const dataToSort: any = filterData?.length > 0 ? filterData : data;
    if (sortOrder === 'asc') {
      return [...dataToSort].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortOrder === 'dsc') {
      return [...dataToSort].sort((a, b) => b.name.localeCompare(a.name));
    }
    return dataToSort;
  };
  

  const buttonStyles = {
    "&.MuiButton-outlined": {
      border: "2px solid #2372B9",
      color: "#2372B9",
      textTransform: "none",
      fontWeight: typographyTokens.fontWeights.bold,
      height: "min-content",
      alignSelf: "center",
      marginRight: "10px",
    },
  };

  const selectStyles = {
    border: "2px solid #2372B9",
    borderRadius: "4px",
    padding: "5px 10px",
    marginRight: "10px",
    fontWeight: typographyTokens.fontWeights.bold,
    color: "#2372B9",
    backgroundColor: isDarkMode? theme.palette.background.default : "#fff",
    cursor: "pointer",
    "&:focus": {
      outline: "none",
      borderColor: "#2372B9",
    },
  };
  

  return (
    <>
        <Box sx={{ minHeight: "500px" }}>
        <Box
          sx={{
            flexDirection: "row",
            display: "flex",
            justifyContent: "space-between",
            paddingRight: "20px",
          }}
        >
          <Box>
            <AdminHeader title="Data Fields" />
          </Box>

          {!isLoading && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  minWidthidth: "210px",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <select onChange={handleSortChange} value={sortOrder} style={selectStyles}>
                  <option value="normal">Sort By Normal</option>
                  <option value="asc">Ascending</option>
                  <option value="dsc">Descending</option>
                </select>
                {filterData.length > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={buttonStyles}
                    onClick={() => {
                      setFilterData([]);
                      setAppliedFilter(null);
                    }}
                  >
                    Reset Filter
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  sx={buttonStyles}
                  onClick={() => setDrawOpen(true)}
                >
                  <i
                    className="fa-regular fa-filter"
                    style={{ paddingRight: "5px" }}
                  ></i>
                  Filter Data Fields
                </Button>
                <Drawer open={drawOpen}>
                  <DrawerFilters
                    setDrawOpen={setDrawOpen}
                    isFilterData={filterDataFields}
                    dataFields={data}
                    labelName={"DataField Name"}
                    appliedFilter={appliedFilter}
                  />
                </Drawer>

                <AddButton
                  tab="Data Field"
                  onAdd={(tab: string) => {
                    setIsOpen(true);
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>

      {isLoading ? (
        <Box pt={10} sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress data-testid="loading-spinner" size={100} />
        </Box>
      ) : (
        <>       
          <MappedListComponent
            data={sortedData()}
            onEditTriggered={handleEditTriggered}
          />
          <ModalWidget
            title={edit === undefined ? 'Add Data Field' : 'Edit Data Field'}
            isOpen={isOpen}
            onClose={() => {
              setIsOpen(false);
              setEdit(undefined);
            }}
            children={
              <AddDataField
                isClose={!isOpen}
                onAdd={(df, op) => {
                  createDataField(df).then((res: any) => {
                    if (op.length > 0 && 'data' in res) {
                      op.forEach((e) => {
                        createDataFieldOptions({
                          ...e,
                          dataFieldID: res.data.id,
                        });
                      });
                    }
                  });
                  setIsOpen(false);
                }}
                onClose={() => setIsOpen(false)}
              />
            }
          />
        </>
      )}
      </Box>
    </>
  );
};

export default DataFields;

const MappedListComponent = React.memo(
  ({
    data,
    onEditTriggered,
  }: {
    data?: DataField[];
    onEditTriggered: (e: DepartmentExtended) => void;
  }) => {
    return (
      <>
        {data?.map((datafield, index) => {
          return (
            <DataFieldScreenCard
              key={index}
              datafield={datafield}
              onEditTriggered={(e: any) => {
                onEditTriggered(e);
              }}
              index ={index}
            />
          );
        })}
      </>
    );
  }
);
